import { useGetList, useTranslate } from 'react-admin';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import type { TicketQueue } from '../../types/chat.types';

export interface QueueSelectorProps {
  value: number | null | undefined;
  onChange: (queueId: number | null) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  allowClear?: boolean;
  required?: boolean;
  error?: boolean;
}

export function QueueSelector({
  value,
  onChange,
  label,
  helperText,
  disabled = false,
  allowClear = true,
  required = false,
  error = false,
}: QueueSelectorProps) {
  const translate = useTranslate();

  const { data: queues, isLoading } = useGetList<TicketQueue>('ticket-queue', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  const handleChange = (event: { target: { value: string | number } }) => {
    const newValue = event.target.value;
    if (newValue === '' || newValue === 'none') {
      onChange(null);
    } else {
      onChange(Number(newValue));
    }
  };

  const selectedQueue = queues?.find((q) => q.id === value);

  return (
    <FormControl fullWidth disabled={disabled} required={required} error={error}>
      <InputLabel id="queue-selector-label">
        {label || translate('resources.channel.settings.defaultQueue')}
      </InputLabel>
      <Select
        labelId="queue-selector-label"
        value={value ?? ''}
        onChange={handleChange}
        label={label || translate('resources.channel.settings.defaultQueue')}
        error={error}
        renderValue={() => {
          if (!value) {
            return (
              <em>{translate('resources.channel.settings.useDefaultQueue')}</em>
            );
          }
          if (selectedQueue) {
            return (
              <Box display="flex" alignItems="center" gap={1}>
                {selectedQueue.color && (
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '4px',
                      backgroundColor: selectedQueue.color,
                      border: '1px solid rgba(0,0,0,0.2)',
                      flexShrink: 0,
                    }}
                  />
                )}
                <span>{selectedQueue.name}</span>
                {selectedQueue.isDefault && (
                  <Chip label="Default" size="small" color="primary" sx={{ ml: 1 }} />
                )}
              </Box>
            );
          }
          return value;
        }}
      >
        {isLoading && (
          <MenuItem disabled>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            {translate('ra.message.loading')}
          </MenuItem>
        )}
        {allowClear && (
          <MenuItem value="">
            <em>{translate('resources.channel.settings.useDefaultQueue')}</em>
          </MenuItem>
        )}
        {queues?.map((queue) => (
          <MenuItem key={queue.id} value={queue.id}>
            <Box display="flex" alignItems="center" gap={1} width="100%">
              {queue.color && (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '4px',
                    backgroundColor: queue.color,
                    border: '1px solid rgba(0,0,0,0.2)',
                    flexShrink: 0,
                  }}
                />
              )}
              <span>{queue.name}</span>
              {queue.isDefault && (
                <Chip label="Default" size="small" color="primary" sx={{ ml: 'auto' }} />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </FormControl>
  );
}

export default QueueSelector;
